import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, StyleSheet,
  SafeAreaView, Switch, Alert, ActivityIndicator, Image,
} from 'react-native';
import { colors, spacing, radius } from '../theme';
import { Divider, CTAButton } from '../components/UI';
import BackButton from '../components/BackButton';
import { catalog, initialWatchlist, initialNotifications } from '../data/stockData';
import { useAuth } from '../context/AuthContext';
import { AWS_CONFIG } from '../config/aws_config';
const API = AWS_CONFIG.apiUrl;

const NOTIF_TYPES = {
  restock:    { label: 'Retour stock', color: '#4A8C5C', bg: '#1A2A1C', border: '#2A5030' },
  price_drop: { label: 'Baisse prix',  color: colors.accent.main, bg: colors.accent.ghost, border: colors.text.faint },
  low_stock:  { label: 'Stock faible', color: '#9B7B5A', bg: '#2A1C0A', border: '#4A3010' },
};

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(diff / 86400000);
  if (h < 1)  return 'À l\'instant';
  if (h < 24) return `Il y a ${h}h`;
  if (d === 1) return 'Hier';
  return `Il y a ${d} jours`;
}

/* ══ TAB : Notifications ══════════════════════════════════ */
function NotifTab({ notifications, onRead, onReadAll, onClear }) {
  const unread = notifications.filter(n => !n.read).length;

  return (
    <>
      <View style={s.tabHeader}>
        <View style={s.tabHeaderLeft}>
          <Text style={s.tabTitle}>Alertes</Text>
          {unread > 0 && (
            <View style={s.unreadBadge}>
              <Text style={s.unreadBadgeText}>{unread}</Text>
            </View>
          )}
        </View>
        <View style={s.tabActions}>
          {unread > 0 && (
            <TouchableOpacity onPress={onReadAll}>
              <Text style={s.actionLink}>Tout lire</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>

      {notifications.length === 0 ? (
        <View style={s.empty}>
          <Text style={s.emptyGlyph}>◈</Text>
          <Text style={s.emptyTitle}>Aucune alerte</Text>
          <Text style={s.emptyText}>Vous serez notifié dès qu'un article surveillé sera disponible</Text>
        </View>
      ) : (
        notifications.map(notif => {
          const type = NOTIF_TYPES[notif.type] || NOTIF_TYPES.restock;
          return (
            <TouchableOpacity
              key={`notif_${notif.id || Math.random()}`}
              style={[s.notifCard, !notif.read && s.notifCardUnread]}
              onPress={() => onRead(notif.id)}
              activeOpacity={0.75}
            >
              {/* Unread dot */}
              {!notif.read && <View style={s.unreadDot} />}

              {/* Emoji + type badge */}
              <View style={s.notifLeft}>
                <View style={[s.notifEmoji, { backgroundColor: type.bg, borderColor: type.border }]}>
                  <Text style={s.notifEmojiText}>{notif.emoji}</Text>
                </View>
              </View>

              {/* Content */}
              <View style={s.notifContent}>
                <View style={s.notifTopRow}>
                  <View style={[s.typeBadge, { backgroundColor: type.bg, borderColor: type.border }]}>
                    <Text style={[s.typeBadgeText, { color: type.color }]}>{type.label}</Text>
                  </View>
                  <Text style={s.notifTime}>{timeAgo(notif.date)}</Text>
                </View>
                <Text style={s.notifTitle}>{notif.title}</Text>
                <Text style={s.notifBody}>{notif.body}</Text>
                <View style={s.notifMeta}>
                  <Text style={s.notifLook}>Look · {notif.lookName}</Text>
                  <View style={s.notifPriceRow}>
                    {notif.oldPrice && (
                      <Text style={s.notifOldPrice}>{notif.oldPrice} €</Text>
                    )}
                    <Text style={s.notifPrice}>{notif.price} €</Text>
                  </View>
                </View>

                {/* Actions */}
                {notif.type === 'restock' && (
                  <View style={s.notifActions}>
                    <TouchableOpacity style={s.notifActionBtn}>
                      <Text style={s.notifActionPrimary}>Voir l'article →</Text>
                    </TouchableOpacity>
                    <TouchableOpacity style={s.notifActionBtnSecond}>
                      <Text style={s.notifActionSecondary}>Ajouter au panier</Text>
                    </TouchableOpacity>
                  </View>
                )}
                {notif.type === 'price_drop' && (
                  <View style={s.notifActions}>
                    <TouchableOpacity style={s.notifActionBtn}>
                      <Text style={s.notifActionPrimary}>Profiter de l'offre →</Text>
                    </TouchableOpacity>
                  </View>
                )}
              </View>
            </TouchableOpacity>
          );
        })
      )}
    </>
  );
}

/* ══ TAB : Watchlist ══════════════════════════════════════ */
function WatchlistTab({ watchlist, onToggleNotify, onRemove, onAdd }) {
  const watched = watchlist.map(w => ({
    ...w,
    product: catalog.find(p => p.id === w.productId),
  })).filter(w => w.product);

  const available = watched.filter(w => w.product.inStock);
  const waiting   = watched.filter(w => !w.product.inStock);

  return (
    <>
      <View style={s.tabHeader}>
        <Text style={s.tabTitle}>Articles surveillés</Text>
        <Text style={s.tabCount}>{watched.length} article{watched.length > 1 ? 's' : ''}</Text>
      </View>

      {watched.length === 0 ? (
        <View style={s.empty}>
          <Text style={s.emptyGlyph}>◈</Text>
          <Text style={s.emptyTitle}>Aucun article surveillé</Text>
          <Text style={s.emptyText}>Ajoutez des articles hors stock depuis vos suggestions pour être alerté dès leur retour</Text>
        </View>
      ) : (
        <>
          {/* ── Disponibles ── */}
          {available.length > 0 && (
            <>
              <View style={s.groupHeader}>
                <View style={s.groupDot} />
                <Text style={s.groupTitle}>Disponibles maintenant</Text>
                <Text style={s.groupCount}>{available.length}</Text>
              </View>
              {available.map(w => (
                <WatchCard
                  key={w.productId}
                  item={w}
                  onToggle={() => onToggleNotify(w.productId)}
                  onRemove={() => onRemove(w.productId)}
                  available
                />
              ))}
            </>
          )}

          {/* ── En attente ── */}
          {waiting.length > 0 && (
            <>
              <View style={s.groupHeader}>
                <View style={[s.groupDot, { backgroundColor: colors.text.faint }]} />
                <Text style={s.groupTitle}>En attente de stock</Text>
                <Text style={s.groupCount}>{waiting.length}</Text>
              </View>
              {waiting.map(w => (
                <WatchCard
                  key={w.productId}
                  item={w}
                  onToggle={() => onToggleNotify(w.productId)}
                  onRemove={() => onRemove(w.productId)}
                  available={false}
                />
              ))}
            </>
          )}
        </>
      )}

      {/* ── Suggestion d'articles hors stock ── */}
      <View style={s.suggestSection}>
        <Text style={s.suggestTitle}>Articles hors stock non suivis</Text>
        {catalog.filter(p => !p.inStock && !watchlist.find(w => w.productId === p.id)).map(p => (
          <View key={`ns_${p.id || Math.random()}`} style={s.suggestCard}>
            <View style={s.suggestLeft}>
              <Text style={s.suggestEmoji}>{p.emoji}</Text>
            </View>
            <View style={s.suggestInfo}>
              <Text style={s.suggestName}>{p.name}</Text>
              <Text style={s.suggestMeta}>{p.brand} · {p.size} · Look : {p.lookName}</Text>
              <Text style={s.suggestPrice}>{p.price} €</Text>
            </View>
            <TouchableOpacity style={s.watchBtn} onPress={() => onAdd(p.id)}>
              <Text style={s.watchBtnText}>Surveiller</Text>
            </TouchableOpacity>
          </View>
        ))}
      </View>
    </>
  );
}

function WatchCard({ item, onToggle, onRemove, available }) {
  const { product, notifyOnRestock, notified, addedAt } = item;
  return (
    <View style={[s.watchCard, available && s.watchCardAvailable]}>
      {/* Stock status indicator */}
      <View style={[s.stockBar, { backgroundColor: available ? '#4A8C5C' : colors.bg.border }]} />

      <View style={s.watchEmoji}>
        <Text style={s.watchEmojiText}>{product.emoji}</Text>
      </View>

      <View style={s.watchInfo}>
        <View style={s.watchTopRow}>
          <Text style={s.watchName}>{product.name}</Text>
          <View style={[
            s.stockBadge,
            available ? s.stockBadgeIn : s.stockBadgeOut,
          ]}>
            <Text style={[s.stockBadgeText, { color: available ? '#4A8C5C' : colors.text.faint }]}>
              {available ? '● En stock' : '○ Hors stock'}
            </Text>
          </View>
        </View>
        <Text style={s.watchMeta}>{product.brand} · {product.size} · {product.price} €</Text>
        <Text style={s.watchLook}>Look · {product.lookName}</Text>

        {available && product.stockDate && (
          <Text style={s.restockDate}>Disponible depuis le {new Date(product.stockDate).toLocaleDateString('fr-FR')}</Text>
        )}

        <View style={s.watchFooter}>
          <View style={s.watchNotifRow}>
            <Text style={s.watchNotifLabel}>Notifications</Text>
            <Switch
              value={notifyOnRestock}
              onValueChange={onToggle}
              trackColor={{ false: colors.bg.border, true: colors.text.faint }}
              thumbColor={notifyOnRestock ? colors.accent.main : colors.text.faint}
              ios_backgroundColor={colors.bg.border}
              style={{ transform: [{ scaleX: 0.7 }, { scaleY: 0.7 }] }}
            />
          </View>
          <View style={s.watchBtns}>
            {available && (
              <TouchableOpacity style={s.watchActionBtn}>
                <Text style={s.watchActionText}>Ajouter au panier</Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity onPress={onRemove}>
              <Text style={s.watchRemoveText}>Retirer</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </View>
  );
}

/* ══ MAIN SCREEN ══════════════════════════════════════════ */
export default function NotificationsScreen({ navigation }) {
  const [tab,           setTab]           = useState('notifs');
  const [notifications, setNotifications] = useState(initialNotifications);
  const { user } = useAuth();
  const [watchlist,     setWatchlist]     = useState(initialWatchlist);
  const [dbAlerts,      setDbAlerts]      = useState([]);
  const [loadingAlerts, setLoadingAlerts] = useState(false);

  // Charger les alertes depuis la DB
  const loadAlerts = useCallback(async () => {
    if (!user?.accessToken) return;
    setLoadingAlerts(true);
    try {
      const res = await fetch(`${API}/alerts`, {
        headers: { Authorization: `Bearer ${user.accessToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDbAlerts(Array.isArray(data) ? data : []);
      }
    } catch(e) {}
    setLoadingAlerts(false);
  }, [user]);

  useEffect(() => { loadAlerts(); }, [loadAlerts]);

  // Supprimer une alerte DB
  const deleteAlert = useCallback(async (alertId) => {
    setDbAlerts(prev => prev.filter(a => a.id !== alertId));
    if (user?.accessToken) {
      fetch(`${API}/alerts/${alertId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${user.accessToken}` },
      }).catch(() => {});
    }
  }, [user]);

  // Ajouter une alerte sur un produit du catalogue
  const addAlert = useCallback(async (product, size) => {
    if (!user?.accessToken) return;
    try {
      const res = await fetch(`${API}/alerts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user.accessToken}` },
        body: JSON.stringify({
          product_id:       product.id,
          product_name:     product.name,
          product_brand:    product.brand,
          product_price:    product.price,
          product_image:    product.image_url || product.image,
          product_partner:  product.partner,
          product_category: product.category,
          target_size:      size || null,
        }),
      });
      if (res.ok) {
        await loadAlerts();
        Alert.alert('✓ Alerte créée', `Vous serez notifié quand ${product.name} sera disponible`);
      }
    } catch(e) {}
  }, [user, loadAlerts]);

  const unreadCount = notifications.filter(n => !n.read).length;

  const markRead    = (id) => setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  const markAllRead = ()   => setNotifications(prev => prev.map(n => ({ ...n, read: true })));

  const toggleNotify = (productId) =>
    setWatchlist(prev => prev.map(w => w.productId === productId ? { ...w, notifyOnRestock: !w.notifyOnRestock } : w));

  const removeWatch = (productId) => {
    Alert.alert('Retirer de la liste', 'Vous ne serez plus alerté pour cet article.', [
      { text: 'Annuler', style: 'cancel' },
      { text: 'Retirer', style: 'destructive', onPress: () => setWatchlist(prev => prev.filter(w => w.productId !== productId)) },
    ]);
  };

  const addWatch = (productId) => {
    if (!watchlist.find(w => w.productId === productId)) {
      setWatchlist(prev => [...prev, {
        productId,
        addedAt: new Date().toISOString().split('T')[0],
        notifyOnRestock: true,
        notified: false,
      }]);
    }
  };

  return (
    <SafeAreaView style={s.safe}>
      

      {/* ── Topbar premium ── */}
      <View style={s.topbarPrem}>
        <BackButton onPress={() => navigation.goBack()} />
        <View style={{ flex: 1, alignItems: 'center' }}>
          <Text style={s.headerBadgePrem}>🔔 NOTIFICATIONS</Text>
          <Text style={s.headerTitlePrem}>Alertes & Stock</Text>
          {unreadCount > 0 && (
            <Text style={s.subtitlePrem}>{unreadCount} nouvelle{unreadCount > 1 ? 's' : ''}</Text>
          )}
        </View>
        <TouchableOpacity
          style={s.settingsBtnPrem}
          onPress={() => navigation.navigate('Profil')}
        >
          <Text style={s.settingsBtnTextPrem}>⚙</Text>
        </TouchableOpacity>
      </View>

      <Divider />

      {/* ── Tabs ── */}
      <View style={s.tabs}>
        <TouchableOpacity
          style={[s.tab, tab === 'notifs' && s.tabActive]}
          onPress={() => setTab('notifs')}
        >
          <Text style={[s.tabLabel, tab === 'notifs' && s.tabLabelActive]}>Notifications</Text>
          {unreadCount > 0 && tab !== 'notifs' && (
            <View style={s.tabBadge}><Text style={s.tabBadgeText}>{unreadCount}</Text></View>
          )}
        </TouchableOpacity>
        <TouchableOpacity
          style={[s.tab, tab === 'watch' && s.tabActive]}
          onPress={() => setTab('watch')}
        >
          <Text style={[s.tabLabel, tab === 'watch' && s.tabLabelActive]}>Mes alertes</Text>
          <Text style={s.tabSub}>{watchlist.length}</Text>
        </TouchableOpacity>
      </View>

      <View style={s.tabLine}>
        <View style={[s.tabUnderline, { left: tab === 'notifs' ? '0%' : '50%' }]} />
      </View>

      <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={s.scroll}>
        {tab === 'notifs' ? (
          <NotifTab
            notifications={notifications}
            onRead={markRead}
            onReadAll={markAllRead}
          />
        ) : (
          <WatchlistTab
            watchlist={watchlist}
            onToggleNotify={toggleNotify}
            onRemove={removeWatch}
            onAdd={addWatch}
          />
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

const s = StyleSheet.create({
  safe:            { flex: 1, backgroundColor: '#FFFFFF' },
  topbar:          { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: spacing.xl, paddingBottom: spacing.md },
  backBtn:         { paddingRight: 12, paddingVertical: 4 },
  backTxt:         { fontSize: 24, color: colors.text.primary, fontWeight: '300' },
  title:           { fontSize: 13, letterSpacing: 4, color: colors.text.secondary, textTransform: 'uppercase' },
  subtitle:        { fontSize: 8, letterSpacing: 2, color: '#4A8C5C', marginTop: 2 },
  settingsBtn:     { width: 30, height: 30, alignItems: 'center', justifyContent: 'center' },
  settingsBtnText: { fontSize: 16, color: colors.text.faint },

  tabs:            { flexDirection: 'row', paddingHorizontal: spacing.xl },
  tab:             { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 10, gap: 6 },
  tabActive:       {},
  tabLabel:        { fontSize: 9, letterSpacing: 2.5, color: colors.text.faint, textTransform: 'uppercase' },
  tabLabelActive:  { color: colors.accent.main },
  tabSub:          { fontSize: 9, color: colors.text.faint },
  tabBadge:        { backgroundColor: '#4A8C5C', borderRadius: 8, paddingHorizontal: 5, paddingVertical: 1 },
  tabBadgeText:    { fontSize: 8, color: '#FFF', fontWeight: '500' },
  tabLine:         { height: 0.5, backgroundColor: colors.bg.border, marginHorizontal: spacing.xl, position: 'relative', marginBottom: spacing.md },
  tabUnderline:    { position: 'absolute', top: -0.5, width: '50%', height: 1, backgroundColor: colors.accent.main },

  scroll:          { paddingHorizontal: spacing.xl, paddingBottom: 32 },

  tabHeader:       { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.md },
  tabHeaderLeft:   { flexDirection: 'row', alignItems: 'center', gap: 8 },
  tabTitle:        { fontSize: 8, letterSpacing: 3, color: colors.text.faint, textTransform: 'uppercase' },
  tabCount:        { fontSize: 8, letterSpacing: 2, color: colors.text.faint },
  tabActions:      { flexDirection: 'row', gap: 12 },
  actionLink:      { fontSize: 8, letterSpacing: 2, color: colors.accent.main, textTransform: 'uppercase' },
  unreadBadge:     { backgroundColor: '#4A8C5C', borderRadius: 8, paddingHorizontal: 6, paddingVertical: 1 },
  unreadBadgeText: { fontSize: 9, color: '#FFF', fontWeight: '500' },

  /* Notification cards */
  notifCard:       { backgroundColor: '#FFFFFF', borderRadius: radius.md, borderWidth: 0.5, borderColor: colors.bg.border, padding: spacing.md, marginBottom: 8, flexDirection: 'row', gap: 10, position: 'relative' },
  notifCardUnread: { borderColor: colors.bg.subtle },
  unreadDot:       { position: 'absolute', top: 10, right: 10, width: 6, height: 6, borderRadius: 3, backgroundColor: '#4A8C5C' },
  notifLeft:       { alignItems: 'center' },
  notifEmoji:      { width: 38, height: 38, borderRadius: radius.sm, alignItems: 'center', justifyContent: 'center', borderWidth: 0.5 },
  notifEmojiText:  { fontSize: 18 },
  notifContent:    { flex: 1 },
  notifTopRow:     { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 },
  typeBadge:       { paddingHorizontal: 7, paddingVertical: 2, borderRadius: radius.pill, borderWidth: 0.5 },
  typeBadgeText:   { fontSize: 7, letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: '500' },
  notifTime:       { fontSize: 8, letterSpacing: 1, color: colors.text.faint },
  notifTitle:      { fontSize: 12, color: colors.text.secondary, letterSpacing: 0.3, marginBottom: 3 },
  notifBody:       { fontSize: 10, color: colors.text.faint, lineHeight: 16, letterSpacing: 0.2 },
  notifMeta:       { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 },
  notifLook:       { fontSize: 8, letterSpacing: 1.5, color: colors.text.faint, textTransform: 'uppercase' },
  notifPriceRow:   { flexDirection: 'row', alignItems: 'baseline', gap: 5 },
  notifOldPrice:   { fontSize: 10, color: colors.text.faint, textDecorationLine: 'line-through' },
  notifPrice:      { fontSize: 13, color: colors.text.primary, fontWeight: '200', letterSpacing: 0.5 },
  notifActions:    { flexDirection: 'row', gap: 8, marginTop: 8 },
  notifActionBtn:  { borderWidth: 0.5, borderColor: colors.accent.main, paddingHorizontal: 10, paddingVertical: 5, borderRadius: radius.sm },
  notifActionPrimary: { fontSize: 8, letterSpacing: 1.5, color: colors.accent.main, textTransform: 'uppercase' },
  notifActionBtnSecond: { borderWidth: 0.5, borderColor: colors.bg.border, paddingHorizontal: 10, paddingVertical: 5, borderRadius: radius.sm },
  notifActionSecondary: { fontSize: 8, letterSpacing: 1.5, color: colors.text.faint, textTransform: 'uppercase' },

  /* Group headers */
  groupHeader:     { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 8, marginTop: 4 },
  groupDot:        { width: 6, height: 6, borderRadius: 3, backgroundColor: '#4A8C5C' },
  groupTitle:      { fontSize: 8, letterSpacing: 2.5, color: colors.text.faint, textTransform: 'uppercase', flex: 1 },
  groupCount:      { fontSize: 9, color: colors.text.faint },

  /* Watch cards */
  watchCard:       { backgroundColor: '#FFFFFF', borderRadius: radius.md, borderWidth: 0.5, borderColor: colors.bg.border, marginBottom: 8, flexDirection: 'row', overflow: 'hidden' },
  watchCardAvailable: { borderColor: '#2A5030' },
  stockBar:        { width: 3 },
  watchEmoji:      { width: 52, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.bg.surface },
  watchEmojiText:  { fontSize: 20 },
  watchInfo:       { flex: 1, padding: spacing.md },
  watchTopRow:     { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 3 },
  watchName:       { fontSize: 12, color: colors.text.secondary, letterSpacing: 0.3, flex: 1, marginRight: 8 },
  stockBadge:      { paddingHorizontal: 7, paddingVertical: 2, borderRadius: radius.pill, borderWidth: 0.5 },
  stockBadgeIn:    { backgroundColor: '#1A2A1C', borderColor: '#2A5030' },
  stockBadgeOut:   { backgroundColor: colors.bg.surface, borderColor: colors.bg.border },
  stockBadgeText:  { fontSize: 8, letterSpacing: 1 },
  watchMeta:       { fontSize: 9, letterSpacing: 1.5, color: colors.text.faint, textTransform: 'uppercase', marginBottom: 2 },
  watchLook:       { fontSize: 8, letterSpacing: 1.5, color: colors.text.faint, textTransform: 'uppercase' },
  restockDate:     { fontSize: 8, letterSpacing: 1, color: '#4A8C5C', marginTop: 3 },
  watchFooter:     { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, paddingTop: 8, borderTopWidth: 0.5, borderTopColor: colors.bg.border },
  watchNotifRow:   { flexDirection: 'row', alignItems: 'center', gap: 5 },
  watchNotifLabel: { fontSize: 8, letterSpacing: 1.5, color: colors.text.faint, textTransform: 'uppercase' },
  watchBtns:       { flexDirection: 'row', alignItems: 'center', gap: 10 },
  watchActionBtn:  { borderWidth: 0.5, borderColor: colors.accent.main, paddingHorizontal: 8, paddingVertical: 4, borderRadius: radius.sm },
  watchActionText: { fontSize: 8, letterSpacing: 1.5, color: colors.accent.main, textTransform: 'uppercase' },
  watchRemoveText: { fontSize: 8, letterSpacing: 1.5, color: colors.text.faint, textTransform: 'uppercase' },

  /* Suggest section */
  suggestSection:  { marginTop: spacing.lg },
  suggestTitle:    { fontSize: 8, letterSpacing: 3, color: colors.text.faint, textTransform: 'uppercase', marginBottom: spacing.sm },
  suggestCard:     { backgroundColor: '#FFFFFF', borderRadius: radius.md, borderWidth: 0.5, borderColor: colors.bg.border, padding: spacing.md, marginBottom: 8, flexDirection: 'row', gap: 10, alignItems: 'center', opacity: 0.7 },
  suggestLeft:     { width: 38, height: 44, backgroundColor: colors.bg.surface, borderRadius: radius.sm, alignItems: 'center', justifyContent: 'center', borderWidth: 0.5, borderColor: colors.bg.border },
  suggestEmoji:    { fontSize: 18 },
  suggestInfo:     { flex: 1 },
  suggestName:     { fontSize: 11, color: colors.text.secondary, letterSpacing: 0.3 },
  suggestMeta:     { fontSize: 8, letterSpacing: 1.5, color: colors.text.faint, textTransform: 'uppercase', marginTop: 2 },
  suggestPrice:    { fontSize: 11, color: colors.accent.main, marginTop: 3 },
  watchBtn:        { borderWidth: 0.5, borderColor: colors.text.faint, paddingHorizontal: 10, paddingVertical: 6, borderRadius: radius.sm },
  watchBtnText:    { fontSize: 8, letterSpacing: 1.5, color: colors.text.secondary, textTransform: 'uppercase' },

  /* Empty */
  empty:           { paddingTop: 40, alignItems: 'center', paddingBottom: 20 },
  emptyGlyph:      { fontSize: 24, color: colors.text.faint, marginBottom: spacing.md },
  emptyTitle:      { fontSize: 11, letterSpacing: 3, color: colors.text.secondary, textTransform: 'uppercase', marginBottom: 6 },
  emptyText:       { fontSize: 10, letterSpacing: 1, color: colors.text.faint, textAlign: 'center', lineHeight: 18, maxWidth: 260 },

  // ═══ Style premium ═══
  topbarPrem: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 16, paddingVertical: 14, backgroundColor: '#FAFAFA',
    borderBottomWidth: 0.5, borderBottomColor: '#E8E0C8',
  },
  backBtnPrem:   { width: 40, height: 40, alignItems: 'center', justifyContent: 'center' },
  backTxtPrem:   { fontSize: 32, color: '#1A1A1A', fontWeight: '300' },
  headerBadgePrem: { fontSize: 9, letterSpacing: 2.5, color: '#B8860B', fontWeight: '700', marginBottom: 4 },
  headerTitlePrem: { fontSize: 18, fontWeight: '300', color: '#1A1A1A', letterSpacing: 0.3 },
  subtitlePrem:  { fontSize: 11, color: '#999', marginTop: 2, letterSpacing: 0.5 },
  settingsBtnPrem:     { width: 40, height: 40, alignItems: 'center', justifyContent: 'center' },
  settingsBtnTextPrem: { fontSize: 22, color: '#1A1A1A' },
});
